import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { NavbarPortal } from '../navbar-portal';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { RefreshFlash } from './RefreshFlash';
import {
  clockTone,
  COUNTDOWN_SECONDS,
  formatClockTime,
  formatHms,
  formatMinutesSeconds,
  hasRoundClock,
  useCountdown,
  type ClockRound,
  type ClockTone,
} from './round-clock';

interface RoundClockProps {
  round: ClockRound;
  /** The running clock reached zero (see useRoundTimeUp). */
  onTimeUp: () => void;
  /** The clock to the start reached zero: time to go and look. */
  onStartReached: () => void;
  /**
   * Whether a round with neither clock nor result yet says "draft". A word for
   * the people who would start it; to somebody waiting to climb it, it says
   * nothing they can act on, so they are shown nothing until there is a clock.
   */
  showDraft?: boolean;
  /**
   * What the page is on inside the round — the athlete being marked, the block
   * being judged — at the head of the strip (see RoundClockSubject). Given, the
   * strip is drawn even when the round has no state to say.
   */
  subject?: ReactNode;
}

const TEXT_TONES: Record<ClockTone, string> = {
  calm: 'text-emerald-700 dark:text-emerald-300',
  soon: 'text-amber-700 dark:text-amber-300',
  urgent: 'text-destructive',
  idle: 'text-muted-foreground',
};

/**
 * Two short pulses as the clock turns red: whoever is at the wall, phone in a
 * pocket or on the mat, learns the last minute has begun without looking.
 */
const LAST_MINUTE_BUZZ = [200, 100, 200];

const BAR_TONES: Record<ClockTone, string> = {
  calm: 'bg-emerald-500',
  soon: 'bg-amber-500',
  urgent: 'bg-destructive',
  idle: 'bg-muted-foreground',
};

/**
 * The round's state and clock as every round page shows it: a slim strip under
 * the navbar, so the navbar's room goes to the breadcrumb — the athlete or the
 * block being scored is the one thing on it that must not be cut short.
 *
 * - Running: the minutes and seconds left, green, then amber in the last five
 *   minutes and red in the last one, when the phone buzzes too; and a hairline on the strip's bottom edge
 *   that empties as the time runs out, in the same colour — readable from the
 *   corner of an eye without reading any digits.
 * - Waiting for its start: the time until it, grey — nothing is running out.
 * - Either way, a clock more than ninety minutes off says the time of day it
 *   ends or starts at instead (COUNTDOWN_SECONDS).
 * - After the clock: the state in a word, "in verification" or "finished".
 *
 * Given a `subject`, the strip opens with it: the athlete or the block the page
 * is on, with the round above it in the breadcrumb.
 *
 * A tap on the time opens the state in full: what the round is doing, and the
 * time in hours, minutes and seconds.
 *
 * Drawn through a portal, so it stays the page's: the page's refresh resets its
 * figure, and reaching zero is the page's to act on (`onTimeUp`). Where there is
 * no navbar it stands where the page puts it.
 */
export function RoundClock({
  round,
  onTimeUp,
  onStartReached,
  showDraft = false,
  subject,
}: RoundClockProps) {
  const { t, i18n } = useTranslation();

  const counting = hasRoundClock(round);
  const runningOut = !!round.running;
  const figure = counting ? ((runningOut ? round.seconds_remaining : round.seconds_until_start) ?? 0) : 0;
  const remaining = useCountdown(figure, counting ? (runningOut ? onTimeUp : onStartReached) : undefined);
  const tone =
    counting ? clockTone(remaining, runningOut) : round.correcting || round.under_review ? 'soon' : 'idle';

  // Only on the clock turning red before the reader's eyes: a page opened in
  // the last minute already shows it, and buzzing on arrival would only startle.
  // Where the browser cannot vibrate (Safari on iPhone) the red is all there is.
  const previousTone = useRef(tone);
  useEffect(() => {
    const was = previousTone.current;
    previousTone.current = tone;
    if (tone === 'urgent' && (was === 'calm' || was === 'soon') && 'vibrate' in navigator) {
      navigator.vibrate(LAST_MINUTE_BUZZ);
    }
  }, [tone]);

  const status = round.running
    ? t('competitionRounds.active')
    : round.correcting
      ? t('competitionRounds.corrections')
      : round.under_review
        ? t('competitionRounds.verification')
        : round.ended
          ? t('competitionRounds.finished')
          : counting
            ? t('competitionRounds.clock.notStarted')
            : showDraft
              ? t('competitionRounds.draft')
              : null;
  if (!status && !subject) return null;

  // What the strip says: the clock while there is one — counted down when it is
  // near, as a time of day when it is not — and the state in a word after.
  const nearby = remaining <= COUNTDOWN_SECONDS;
  const lead = counting && remaining > 0 && nearby
    ? runningOut ? t('competitionRounds.clock.timeLeft') : t('competitionRounds.clock.startsIn')
    : null;
  const chipText = counting
    ? remaining <= 0 && !runningOut
      ? t('competitionRounds.startingShortly')
      : nearby
        ? formatMinutesSeconds(remaining)
        : t(runningOut ? 'competitionRounds.clock.endsAt' : 'competitionRounds.clock.startsAt', {
            time: formatClockTime(remaining, i18n?.language),
          })
    : status;
  const ticking = counting && (nearby || remaining <= 0);

  const durationSeconds = (round.duration_minutes ?? 0) * 60;
  const showBar = runningOut && counting && durationSeconds > 0;
  const left = showBar ? Math.min(1, Math.max(0, remaining / durationSeconds)) : 0;

  return (
    <NavbarPortal slot="strip">
      <div className="relative border-t border-border/60" data-testid="round-clock-strip">
        <div className={cn('container flex items-center gap-3 px-4 text-xs sm:px-6', subject ? 'h-11' : 'h-8')}>
          {subject}
          {status && (
            /* Lit when the panel moves the round on, rather than when this
               page's own clock reaches zero. */
            <RefreshFlash
              value={[round.running, round.correcting, round.under_review, round.ended, round.closed].join('|')}
              className="ml-auto shrink-0"
            >
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-baseline gap-1.5 rounded-md px-1.5 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={t('competitionRounds.clock.details')}
                    data-testid="round-clock-chip"
                    data-tone={tone}
                  >
                    {lead && <span className="text-muted-foreground">{lead}</span>}
                    <span
                      className={cn(
                        'font-semibold tabular-nums',
                        ticking && 'font-mono text-sm',
                        TEXT_TONES[tone],
                        tone === 'urgent' && 'motion-safe:animate-pulse'
                      )}
                      data-testid={counting ? 'round-countdown' : 'round-status'}
                    >
                      {chipText}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-60 p-3" data-testid="round-clock-details">
                  <p className="text-sm font-semibold">{status}</p>
                  {counting && (
                    <p className="mt-1 flex items-baseline justify-between gap-3 text-sm text-muted-foreground">
                      {runningOut ? t('competitionRounds.clock.timeLeft') : t('competitionRounds.clock.startsIn')}
                      <span className="font-mono text-base font-semibold tabular-nums text-foreground" role="timer">
                        {formatHms(remaining)}
                      </span>
                    </p>
                  )}
                  {round.correcting && (
                    <p className="mt-1 text-sm text-muted-foreground">{t('competitionRounds.clock.correctingHelp')}</p>
                  )}
                  {round.under_review && (
                    <p className="mt-1 text-sm text-muted-foreground">{t('competitionRounds.clock.underReviewHelp')}</p>
                  )}
                </PopoverContent>
              </Popover>
            </RefreshFlash>
          )}
        </div>

        {/* On the navbar's bottom border, which the strip sits on. */}
        {showBar && (
          <div className="absolute inset-x-0 -bottom-px h-[3px] bg-transparent" data-testid="round-clock-bar">
            <div
              className={cn('h-full transition-[width] duration-1000 ease-linear', BAR_TONES[tone])}
              style={{ width: `${left * 100}%` }}
            />
          </div>
        )}
      </div>
    </NavbarPortal>
  );
}

interface RoundClockSubjectProps {
  label: string;
  /** Opens the list to pick another one from; without it the name is just said. */
  onPick?: () => void;
}

/**
 * The athlete or block a round page is on, as the head of the clock's strip:
 * the one name on the page that must never be cut short, and — with chevrons
 * saying so — the way to the next one, which is a far more common move than
 * going back to the round.
 */
export function RoundClockSubject({ label, onPick }: RoundClockSubjectProps) {
  const text = <span className="truncate">{label}</span>;

  if (!onPick) {
    return (
      <span className="flex min-w-0 text-base font-semibold" data-testid="round-clock-subject">
        {text}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={onPick}
      className="-ml-1.5 flex h-9 min-w-0 items-center gap-1 rounded-md px-1.5 text-base font-semibold transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid="round-clock-subject"
    >
      {text}
      <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  );
}
